import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import {
  getListingsForAdmin,
  updateListingByAdmin,
  closeListingByAdmin,
  openListingByAdmin,
  approveListingByAdmin
} from '../../util/api';
import { parse } from '../../util/urlHelpers';
import { denormalisedResponseEntities } from '../../util/data';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED
} from '../../util/types';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import omit from 'lodash/omit';


// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 10;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/EditListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/EditListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/EditListingsPage/FETCH_LISTINGS_ERROR';

export const UPLOAD_IMAGE_REQUEST = 'app/EditListingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/EditListingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/EditListingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_LISTING_REQUEST = 'app/EditListingsPage/UPDATE_LISTING_REQUEST';
export const UPDATE_LISTING_SUCCESS = 'app/EditListingsPage/UPDATE_LISTING_SUCCESS';
export const UPDATE_LISTING_ERROR = 'app/EditListingsPage/UPDATE_LISTING_ERROR';

export const OPEN_LISTING_REQUEST = 'app/EditListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/EditListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/EditListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/EditListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/EditListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/EditListingsPage/CLOSE_LISTING_ERROR';

export const APPROVE_LISTING_REQUEST = 'app/EditListingsPage/APPROVE_LISTING_REQUEST';
export const APPROVE_LISTING_SUCCESS = 'app/EditListingsPage/APPROVE_LISTING_SUCCESS';
export const APPROVE_LISTING_ERROR = 'app/EditListingsPage/APPROVE_LISTING_ERROR';

export const REMOVE_LISTING_IMAGE = 'app/EditListingsPage/REMOVE_LISTING_IMAGE';
export const CLEAR_LISTING_DATA = 'app/EditListingsPage/CLEAR_LISTING_DATA';
export const SET_UPDATED_LISTING = 'app/EditListingsPage/SET_UPDATED_LISTING';

// ================ Reducer ================ //

const initialState = {
  fetchListingsInProgress: false,
  fetchListingsError: null,
  currentPageResultIds: [],
  pagination: null,

  uploadedImages: {},
  uploadedImagesOrder: [],
  uploadImageError: null,
  removedImageIds: [],

  updatingListing: null,
  updateListingError: null,
  updateListingSuccess: false,
  updatedListings: {},

  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  approvingListing: null,
  approvingListingError: null,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted)
    .map(l => l.id);
};

const adminListingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        fetchListingsInProgress: true,
        fetchListingsError: null,
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        fetchListingsInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, fetchListingsInProgress: false, fetchListingsError: payload };

    case UPLOAD_IMAGE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedImages = {
        ...state.uploadedImages,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedImages,
        uploadedImagesOrder: state.uploadedImagesOrder.concat([payload.params.id]),
        uploadImageError: null,
      };
    }
    case UPLOAD_IMAGE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, ...rest } = payload;
      const uploadedImages = { ...state.uploadedImages, [id]: { id, ...rest } };
      return { ...state, uploadedImages };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      const uploadedImages = omit(state.uploadedImages, id);
      return { ...state, uploadedImagesOrder, uploadedImages, uploadImageError: error };
    }

    case UPDATE_LISTING_REQUEST:
      return {
        ...state,
        updatingListing: payload,
        updateListingError: null,
      };
    case UPDATE_LISTING_SUCCESS:
      return {
        ...state,
        updateListingSuccess: payload,
        updatingListing: null,
        uploadedImages: {}
      };
    case UPDATE_LISTING_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, updatingListing: null, updateListingError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...state,
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...state,
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case APPROVE_LISTING_REQUEST:
      return {
        ...state,
        approvingListing: payload,
        approvingListingError: null,
      };
    case APPROVE_LISTING_SUCCESS:
      return {
        ...state,
        approvingListing: null,
      };
    case APPROVE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        approvingListing: null,
        approvingListingError: {
          listingId: state.approvingListingError,
          error: payload,
        },
      };
    }

    case REMOVE_LISTING_IMAGE: {
      const id = payload.imageId;
      // Only mark the image removed if it hasn't been added to the
      // listing already
      const removedImageIds = state.uploadedImages[id]
        ? state.removedImageIds
        : state.removedImageIds.concat(id);

      // Always remove from the draft since it might be a new image to
      // an existing listing.
      const uploadedImages = omit(state.uploadedImages, id);
      const uploadedImagesOrder = state.uploadedImagesOrder.filter(i => i !== id);
      return { ...state, uploadedImages, uploadedImagesOrder, removedImageIds };
    }

    case CLEAR_LISTING_DATA: {
      return {
        ...state,
        uploadedImages: {},
        uploadedImagesOrder: [],
        uploadImageError: null,
        removedImageIds: [],
      };
    };
    case SET_UPDATED_LISTING: {
      return {
        ...state,
        updatedListings: {
          ...state.updatedListings,
          ...payload,
        },
      };
    }

    default:
      return state;
  }
};

export default adminListingPageReducer;

// ================ Action creators ================ //

export const fetchListingsRequest = searchParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const fetchListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response?.data },
});

export const fetchListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const uploadImageRequest = params => ({
  type: UPLOAD_IMAGE_REQUEST,
  payload: { params },
});

export const uploadImageSuccess = result => ({
  type: UPLOAD_IMAGE_SUCCESS,
  payload: result.data
});

export const uploadImageError = e => ({
  type: UPLOAD_IMAGE_ERROR,
  error: true,
  payload: e,
});

export const removeListingImage = imageId => ({
  type: REMOVE_LISTING_IMAGE,
  payload: { imageId },
});

export const clearListingData = () => ({
  type: CLEAR_LISTING_DATA,
});

export const updateListingRequest = payload => ({
  type: UPDATE_LISTING_REQUEST,
  payload
});

export const updateListingSuccess = payload => ({
  type: UPDATE_LISTING_SUCCESS,
  payload
});

export const setUpdatedListing = payload => ({
  type: SET_UPDATED_LISTING,
  payload
});

export const updateListingError = e => ({
  type: UPDATE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const openListingRequest = payload => ({
  type: OPEN_LISTING_REQUEST,
  payload,
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = payload => ({
  type: CLOSE_LISTING_REQUEST,
  payload,
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const approveListingRequest = payload => ({
  type: APPROVE_LISTING_REQUEST,
  payload,
});

export const approveListingSuccess = response => ({
  type: APPROVE_LISTING_SUCCESS,
  payload: response.data,
});

export const approveListingError = e => ({
  type: APPROVE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(fetchListingsRequest(searchParams));

  return getListingsForAdmin({ searchParams })
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchListingsSuccess(response));

      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

const getImageVariantInfo = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  const fieldsImage = [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`];

  return {
    fieldsImage,
    imageVariants: {
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    },
  };
};

export function requestImageUpload(actionPayload, listingImageConfig, listingId) {

  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    const imageVariantInfo = getImageVariantInfo(listingImageConfig);
    const queryParams = {
      expand: true,
      'fields.image': imageVariantInfo.fieldsImage,
      ...imageVariantInfo.imageVariants,
    };

    dispatch(uploadImageRequest(actionPayload));
    return sdk.images
      .upload({ image: actionPayload.file }, queryParams)
      .then(resp => {
        const img = resp.data.data;
        // Uploaded image has an existing id that refers to file
        // The UUID was created as a consequence of this upload call - it's saved to imageId property
        return dispatch(
          uploadImageSuccess({ data: { ...img, id, imageId: img.id, file: actionPayload.file, listingId } })
        );
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

export const updateListing = (listingId, images, currentUserId) => (dispatch, getState, sdk) => {
  dispatch(updateListingRequest(listingId));

  // Return an array of image ids
  const imageIds = images => {
    // For newly uploaded image the UUID can be found from "img.imageId"
    // and for existing listing images the id is "img.id"
    return images ? images.map(img => img.imageId || img.id) : null;
  };

  // If images should be saved, create array out of the image UUIDs for the API call
  const imageProperty = typeof images !== 'undefined' ? { images: imageIds(images) } : {};

  return updateListingByAdmin({ params: { id: listingId, ...imageProperty }, userId: currentUserId })
    .then(response => {
      dispatch(updateListingSuccess(true));
      setTimeout(() => {
        dispatch(updateListingSuccess(false));
      }, 2000);

      const updatedListing = denormalisedResponseEntities(response?.updatedListing);
      dispatch(setUpdatedListing({
        [listingId]:
        {
          images: updatedListing?.[0]?.images,
          state: updatedListing?.[0]?.attributes?.state
        }
      }));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(updateListingError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const closeListing = (listingId, userId) => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return closeListingByAdmin({ listingId, userId })
    .then(response => {
      dispatch(closeListingSuccess(response));

      const updatedListing = denormalisedResponseEntities(response?.updatedListing);
      dispatch(setUpdatedListing({
        [listingId]:
        {
          images: updatedListing?.[0]?.images,
          state: updatedListing?.[0]?.attributes?.state
        }
      }));
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = (listingId, userId) => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return openListingByAdmin({ listingId, userId })
    .then(response => {
      dispatch(openListingSuccess(response));

      const updatedListing = denormalisedResponseEntities(response?.updatedListing);
      dispatch(setUpdatedListing({
        [listingId]:
        {
          images: updatedListing?.[0]?.images,
          state: updatedListing?.[0]?.attributes?.state
        }
      }));
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const approveListing = (listingId, userId) => (dispatch, getState, sdk) => {
  dispatch(approveListingRequest(listingId));

  return approveListingByAdmin({ listingId, userId })
    .then(response => {
      dispatch(approveListingSuccess(response));

      const updatedListing = denormalisedResponseEntities(response?.updatedListing);
      dispatch(setUpdatedListing({
        [listingId]:
        {
          images: updatedListing?.[0]?.images,
          state: updatedListing?.[0]?.attributes?.state
        }
      }));
      return response;
    })
    .catch(e => {
      dispatch(approveListingError(storableError(e)));
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized

  const queryParams = parse(search);
  const { page = 1 } = queryParams;

  const searchListingsCall = searchListings(
    {
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'author.profileImage', 'images'],
      states: `${LISTING_STATE_PENDING_APPROVAL}, ${LISTING_STATE_PUBLISHED}, ${LISTING_STATE_CLOSED}`,
      'fields.listing': [
        'title',
        'deleted',
        'state',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.default',
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.scaled-large',
        'variants.scaled-xlarge',
        
        // Avatars
        'variants.square-small',
        'variants.square-small2x',
      ],
    },
    config
  );
  return dispatch(searchListingsCall);
};
